import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import {
  AwaitingReturnModel,
  WarehouseInwardModel,
} from "../model/warehouse_inward_models";
import { fetchWarehouseInwards } from "../../../../erp/accounts/vouchers/purchase/service/inward_repo";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";
import { useSessionFilters } from "./useSessionFilters";

export interface InwardFilters {
  search?: string;
  status?: string[];
  type?: string[];
  fromDate?: string;
  toDate?: string;
  fromWarehouse?: string;
  toWarehouse?: string;
  itemSearch?: string;
  sortBy?: "value";
  sortOrder?: "asc" | "desc";
  page?: number;
}

export interface InwardPagination {
  page: number;
  totalPages: number;
  total: number;
  limit: number;
  hasMore: boolean;
}

export interface InwardStatusCount {
  pending: number;
  partial: number;
  received: number;
  disputed: number;
  returnable: number;
  replacements: number;
}

interface WarehouseInwardContextType {
  // Core Data
  inwardList: WarehouseInwardModel[];
  awaitingReturns: AwaitingReturnModel[];
  selectedInward: WarehouseInwardModel | null;
  setSelectedInward: React.Dispatch<
    React.SetStateAction<WarehouseInwardModel | null>
  >;
  // Current inward order being processed (for receive page)
  currentInwardOrder: WarehouseInwardModel | null;
  setCurrentInwardOrder: React.Dispatch<
    React.SetStateAction<WarehouseInwardModel | null>
  >;

  // Loading States
  isLoading: boolean;
  isRefreshing: boolean;

  // Filters & Pagination
  filters: InwardFilters;
  setFilters: React.Dispatch<React.SetStateAction<InwardFilters>>;
  pagination: InwardPagination;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;

  // Status Counts
  statusCount: InwardStatusCount;

  // Modal States ONLY
  isReceiveModalOpen: boolean;
  setReceiveModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isViewModalOpen: boolean;
  setViewModalOpen: React.Dispatch<React.SetStateAction<boolean>>;

  // Actions
  refreshInwards: () => Promise<void>;
  openReceiveModal: (inward: WarehouseInwardModel) => void;
  openViewModal: (inward: WarehouseInwardModel) => void;
  closeModals: () => void;
}

const defaultContext: WarehouseInwardContextType = {
  inwardList: [],
  awaitingReturns: [],
  selectedInward: null,
  setSelectedInward: () => {},
  currentInwardOrder: null,
  setCurrentInwardOrder: () => {},
  isLoading: false,
  isRefreshing: false,
  filters: {},
  setFilters: () => {},
  pagination: { page: 1, totalPages: 1, total: 0, limit: 50, hasMore: false },
  page: 1,
  setPage: () => {},
  statusCount: {
    pending: 0,
    partial: 0,
    received: 0,
    disputed: 0,
    returnable: 0,
    replacements: 0,
  },
  isReceiveModalOpen: false,
  setReceiveModalOpen: () => {},
  isViewModalOpen: false,
  setViewModalOpen: () => {},
  refreshInwards: async () => {},
  openReceiveModal: () => {},
  openViewModal: () => {},
  closeModals: () => {},
};

const WarehouseInwardContext =
  createContext<WarehouseInwardContextType>(defaultContext);

export const useWarehouseInward = () => {
  const context = useContext(WarehouseInwardContext);
  if (!context) {
    throw new Error(
      "useWarehouseInward must be used within WarehouseInwardProvider",
    );
  }
  return context;
};

export const WarehouseInwardProvider: React.FC<{
  children: React.ReactNode;
  storageKey?: string;
}> = ({ children, storageKey }) => {
  const { showToast } = useToast();

  const [inwardList, setInwardList] = useState<WarehouseInwardModel[]>([]);
  const [awaitingReturns, setAwaitingReturns] = useState<AwaitingReturnModel[]>(
    [],
  );
  const [selectedInward, setSelectedInward] =
    useState<WarehouseInwardModel | null>(null);
  const [currentInwardOrder, setCurrentInwardOrder] =
    useState<WarehouseInwardModel | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [filters, setFilters] = useSessionFilters<InwardFilters>(
    storageKey,
    {},
  );
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState<InwardPagination>({
    page: 1,
    totalPages: 1,
    total: 0,
    limit: 50,
    hasMore: false,
  });
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);
  const [debouncedItemSearch, setDebouncedItemSearch] = useState(
    filters.itemSearch,
  );

  const [statusCount, setStatusCount] = useState<InwardStatusCount>({
    pending: 0,
    partial: 0,
    received: 0,
    disputed: 0,
    returnable: 0,
    replacements: 0,
  });

  const [isReceiveModalOpen, setReceiveModalOpen] = useState(false);
  const [isViewModalOpen, setViewModalOpen] = useState(false);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedSearch(filters.search?.trim()),
      400,
    );
    return () => clearTimeout(timer);
  }, [filters.search]);

  // Debounce itemSearch
  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedItemSearch(filters.itemSearch?.trim()),
      400,
    );
    return () => clearTimeout(timer);
  }, [filters.itemSearch]);

  // Memoize refreshInwards to prevent recreation on every render
  const refreshInwards = useCallback(async () => {
    const loadingState = page === 1 ? setIsLoading : setIsRefreshing;
    loadingState(true);

    try {
      const res = await fetchWarehouseInwards({
        ...filters,
        search: debouncedSearch?.trim(),
        itemSearch: debouncedItemSearch?.trim(),
        status: filters.status?.length ? filters.status.join(",") : undefined,
        type: filters.type?.length ? filters.type.join(",") : undefined,
        fromWarehouse: filters.fromWarehouse?.trim(),
        toWarehouse: filters.toWarehouse?.trim(),
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
        page,
      });

      if (!res.success) {
        showToast({ type: ToastType.error, text: res.error });
        return;
      }

      setInwardList(res.data);
      setAwaitingReturns(res.awaitingReturns || []);

      if (res.pagination) {
        setPagination(res.pagination);
      }

      setStatusCount({
        pending: res.combinedStatusCount?.pending ?? 0,
        partial: res.combinedStatusCount?.partial ?? 0,
        received: res.combinedStatusCount?.received ?? 0,
        disputed: res.combinedStatusCount?.disputed ?? 0,
        returnable: res.combinedStatusCount?.returnable ?? 0,
        replacements: res.combinedStatusCount?.replacements ?? 0,
      });
    } catch (error: any) {
      showToast({
        type: ToastType.error,
        text: error.message || "Failed to load inwards",
      });
    } finally {
      loadingState(false);
    }
  }, [page, debouncedSearch, debouncedItemSearch, filters]);

  // Fetch inwards on mount and when dependencies change
  useEffect(() => {
    refreshInwards();
  }, [
    page,
    debouncedSearch,
    debouncedItemSearch,
    filters.status,
    filters.type,
    filters.fromDate,
    filters.toDate,
    filters.fromWarehouse,
    filters.toWarehouse,
    filters.sortBy,
    filters.sortOrder,
  ]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
    setSelectedInward(null); // Clear selection on filter change
    closeModals(); // Close modals on filter change
  }, [filters.status, filters.type, debouncedSearch]);

  const openReceiveModal = useCallback((inward: WarehouseInwardModel) => {
    setSelectedInward(inward);
    setReceiveModalOpen(true);
  }, []);

  const openViewModal = useCallback((inward: WarehouseInwardModel) => {
    setSelectedInward(inward);
    setViewModalOpen(true);
  }, []);

  const closeModals = useCallback(() => {
    setReceiveModalOpen(false);
    setViewModalOpen(false);
    setSelectedInward(null);
  }, []);

  const value: WarehouseInwardContextType = useMemo(
    () => ({
      inwardList,
      awaitingReturns,
      selectedInward,
      setSelectedInward,
      currentInwardOrder,
      setCurrentInwardOrder,
      isLoading,
      isRefreshing,
      filters,
      setFilters,
      pagination,
      page,
      setPage,
      statusCount,
      isReceiveModalOpen,
      setReceiveModalOpen,
      isViewModalOpen,
      setViewModalOpen,
      refreshInwards,
      openReceiveModal,
      openViewModal,
      closeModals,
    }),
    [
      inwardList,
      awaitingReturns,
      selectedInward,
      currentInwardOrder,
      isLoading,
      isRefreshing,
      filters,
      pagination,
      page,
      statusCount,
      isReceiveModalOpen,
      isViewModalOpen,
      refreshInwards,
      openReceiveModal,
      openViewModal,
      closeModals,
    ],
  );

  return (
    <WarehouseInwardContext.Provider value={value}>
      {children}
    </WarehouseInwardContext.Provider>
  );
};
